import { merge } from 'lodash';
import { routes } from '../constants/config';
import request from '../utils/request_ajax';
import { initFilestackClient } from './initFilestackClient';

/**
 * Converts a Filestack upload record to a shape that our backend expects
 *
 * @param {object} upload The upload record returned from Filestack
 */
function getImageData(upload) {
  return {
    upload_file_name: upload.filename,
    upload_content_type: upload.mimetype,
    upload_file_size: upload.size,
    upload_key: upload.key,
    upload_source: upload.source,
    upload_container: upload.container,
    upload_filestack_handle: upload.handle,
  };
}

/**
 * Upload an image file to Filestack using Filestack.upload. This is a direct
 * File upload without using the Filestack Picker UI
 *
 * @param {File} file The image file to be uploaded
 * @return {Promise<object>} An object representing the filestack image record
 * with the keys transformed to a format that our backend expects (see: getImageData)
 */
export async function uploadImage(file) {
  const { client, storeOptions } = await initFilestackClient(() =>
    request.get(routes.IMAGES_UPLOAD_OPTIONS),
  );
  const upload = await client.upload(file, {}, storeOptions);
  return getImageData(upload);
}

/**
 * Initializes a Filestack upload dialog
 *
 * @return {Promise<object>} A Promise that resolves with the Filestack record
 */
async function initFilestackImageUploadPicker() {
  const { client, uploadOptions, storeOptions } = await initFilestackClient(
    () => request.get(routes.IMAGES_UPLOAD_OPTIONS),
  );
  return new Promise(async (resolve, reject) => {
    const picker = client.picker({
      maxFiles: 1,
      accept: uploadOptions.extensions,
      imageMax: uploadOptions.image_max_dimensions,
      maxSize: uploadOptions.max_size,
      fromSources: uploadOptions.services,
      onFileUploadFailed: (file, error) => reject(error),
      onUploadDone: response => resolve(response.filesUploaded[0]),
      startUploadingWhenMaxFilesReached: true,
      storeTo: storeOptions,
      /**
       * As of Feb 2020, enabling these transformation options breaks the URL
       * upload functionality due to some security issues. This is a known issue
       * with Filestack. See https://github.com/filestack/filestack-js/issues/186
       *
       * If these options are to be enabled, please double check that URL
       * uploads work without any issues.
       */
      transformations: {
        circle: false,
        crop: false,
        rotate: false,
      },
    });
    picker.open();
  });
}

/**
 * @typedef AssociatedImageParams
 * @property {number} content_id
 * @property {string} content_type
 * @property {string} image_type
 */

/**
 * Uploads an image to Filestack using the Filestack Picker with a content
 * resource associated with the image (e.g. adding a cover image to a lesson)
 *
 * @param {AssociatedImageParams} associatedImageParams An object describing the
 * content with which the uploaded image will be associated
 * @return {Promise<object>} A promise that resolves with the server response of
 * creating a new image
 */
async function filestackImageUpload(associatedImageParams) {
  const upload = await initFilestackImageUploadPicker();
  const image = merge(getImageData(upload), associatedImageParams);
  return request.post(routes.IMAGES, { image });
}

/**
 * Uploads an image to Filestack using the Filestack Picker. Unlike
 * fileStackImageUpload, this will not associate a content to the uploaded image
 * (e.g. adding an image to the image library)
 *
 * @return {Promise<object>} A promise that resolves with the server response of
 * creating a new image
 */
filestackImageUpload.withoutAssociation = async () => {
  const upload = await initFilestackImageUploadPicker();
  return request.post(routes.IMAGES_NEW_IMAGE, { image: getImageData(upload) });
};

/**
 * Updates an existing image by uploading a new one to Filestack using the
 * Filestack Picker and replacing the reference of the old image with that of
 * the new one in our database.
 *
 * @param {{ id: number }} image An object representing the image record to be
 * updated
 * @return {Promise<object>} A Promise that resolves with the server response of
 * updating an image
 */
filestackImageUpload.updateImage = async image => {
  const upload = await initFilestackImageUploadPicker();
  return request.put(routes.IMAGE(image.id), { image: getImageData(upload) });
};

export default filestackImageUpload;
